import { useMutation } from "@tanstack/react-query";

import { createServiceResponseSchema, privateApi } from "@/api/axios";
import { queryClient } from "@/config/query-client";
import { pendingActionsQueryOptions } from "@/domains/profile/factories/pending-actions-query-options";
import { progressQueryOptions } from "@/domains/progress/factories/progress-query-options";
import { POST_SURVEY_DESTINATION } from "@/domains/surveys/models/constants";
import { checkSurveyCompletionResponseSchema } from "@/domains/surveys/models/schemas";
import { captureException } from "@/utils/error-reporting";

export const useCheckSurveyCompletion = () => {
  return useMutation({
    mutationFn: checkCompletion,
    retry: 12,
    retryDelay: 5000,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: pendingActionsQueryOptions.all.queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: progressQueryOptions.current.queryKey,
      });
    },
  });
};

/** `safeParse`, not `parse`: throwing would cost twelve retries over a minute of the loading bar. */
export const checkCompletion = async () => {
  const response = await privateApi.get("/survey/check/completion");

  const parsed = createServiceResponseSchema(checkSurveyCompletionResponseSchema).safeParse(
    response.data,
  );

  if (!parsed.success) {
    /* Reported, not swallowed: a name this build does not know would strand every patient silently. */
    captureException(parsed.error);

    return POST_SURVEY_DESTINATION.CAREPLAN;
  }

  /* Absent is the pre-ticket backend — expected for a deploy window, not worth reporting. */
  return parsed.data.data?.destination ?? POST_SURVEY_DESTINATION.CAREPLAN;
};
